<script setup lang="ts">
	import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
	import { useI18n } from 'vue-i18n'
	import dayjs from 'dayjs'
	import { Motion, AnimatePresence } from 'motion-v'
	import { CircleCheck, CircleAlert, CircleX, Info, X, Trash2 } from 'lucide-vue-next'
	import {
		useToasts,
		useToastPosition,
		removeToast,
		pauseToast,
		resumeToast,
		clearAllToasts,
		type ToastType,
		type ToastItem,
	} from '@/libs/toaster'
	import { _translations } from './_translations'

	const { t } = useI18n(_translations)

	const items = useToasts()
	const position = useToastPosition()
	const expanded = ref(false)
	const windowFocused = ref(typeof document !== 'undefined' ? document.hasFocus() : true)

	const MAX_VISIBLE = 4 // visible limit — expanded and collapsed both cap at 4 (newest + 3 peek)
	const MAX_VISIBLE_COLLAPSED = 4
	const TOAST_HEIGHT_ESTIMATE = 52 // px — initial estimate; overwritten by actual measurements
	const EXPANDED_GAP_PX = 6

	// Toast heights measured ONCE on mount — no reactive updates to avoid animation stutter
	const itemHeights = ref<Record<string, number>>({})

	const setItemRef = (el: Element | null, id: string) => {
		if (el instanceof HTMLElement && !(id in itemHeights.value)) {
			requestAnimationFrame(() => {
				itemHeights.value = { ...itemHeights.value, [id]: el.offsetHeight }
			})
		}
	}

	const getItemHeight = (id: string) => itemHeights.value[id] ?? TOAST_HEIGHT_ESTIMATE

	// Relative timestamp ticks every second while any toasts are on screen so the "5s ago"
	// threshold becomes visible at the right moment.
	const now = ref(Date.now())
	let nowTimer: ReturnType<typeof setInterval> | null = null
	const handleWindowBlur = () => {
		windowFocused.value = false
	}
	const handleWindowFocus = () => {
		windowFocused.value = true
		now.value = Date.now()
	}

	onMounted(() => {
		nowTimer = setInterval(() => {
			if (items.value.length > 0) now.value = Date.now()
		}, 1000)
		window.addEventListener('blur', handleWindowBlur)
		window.addEventListener('focus', handleWindowFocus)
	})
	onUnmounted(() => {
		if (nowTimer) {
			clearInterval(nowTimer)
			nowTimer = null
		}
		if (collapseTimer) {
			clearTimeout(collapseTimer)
			collapseTimer = null
		}
		window.removeEventListener('blur', handleWindowBlur)
		window.removeEventListener('focus', handleWindowFocus)
	})

	const relativeTime = (createdAt: number) => {
		const diffMs = now.value - createdAt
		if (diffMs < 60000) return ''
		return dayjs(createdAt).from(dayjs(now.value))
	}
	// Collapsed stack: asymptotic curve so deeper layers converge (no infinite upward drift).
	// y ≈ COLLAPSED_Y_MAX * (1 - e^(-depth * COLLAPSED_RATE)) → deepest layers overlap fully.
	const COLLAPSED_Y_MAX = 32
	const COLLAPSED_RATE = 0.45
	const COLLAPSED_SCALE_MAX = 0.15 // max shrink → down to 0.85
	const COLLAPSED_CLIP_HEIGHT_PX = 50 // uniform clipped height for stacked older toasts

	const asymptote = (depth: number, max: number, rate = COLLAPSED_RATE) => max * (1 - Math.exp(-depth * rate))

	const isTop = computed(() => position.value.startsWith('top'))
	const isRight = computed(() => position.value.endsWith('right'))
	const isCenter = computed(() => position.value.endsWith('center'))

	const containerPositionClass = computed(() => {
		const vertical = isTop.value ? 'top-4' : 'bottom-4'
		if (isCenter.value) return `${vertical} left-1/2 -translate-x-1/2`
		return isRight.value ? `${vertical} right-4` : `${vertical} left-4`
	})

	const enterOffset = computed(() => {
		if (isCenter.value) return { y: isTop.value ? -60 : 60 }
		return { x: isRight.value ? 80 : -80 }
	})

	const transformOriginStyle = computed(() => {
		const vertical = isTop.value ? 'top' : 'bottom'
		if (isCenter.value) return `${vertical} center`
		const horizontal = isRight.value ? 'right' : 'left'
		return `${vertical} ${horizontal}`
	})

	// For top: reverse so newest renders at the top anchor, older below. For bottom: natural
	// order so newest renders at the bottom anchor, older above. Both place newest AT the anchor.
	const visibleItems = computed(() => {
		const slice = items.value.slice(-MAX_VISIBLE)
		return isTop.value ? slice.slice().reverse() : slice
	})

	// depth 0 = newest (at the anchor), higher depth = older (farther from anchor)
	const depthOf = (i: number) => (isTop.value ? i : visibleItems.value.length - 1 - i)

	const shouldStack = computed(() => !expanded.value && visibleItems.value.length > 1)

	// Y translate per item. Newest (depth 0) sits AT the anchor (y=0). Older items extend
	// AWAY from the anchor — positive y for top-anchored (downward), negative y for bottom.
	const yForItem = (i: number) => {
		const depth = depthOf(i)
		if (depth === 0) return 0
		const direction = isTop.value ? 1 : -1
		if (shouldStack.value) {
			// Collapsed: uniform peek based on depth, independent of actual content heights.
			const effectiveDepth = Math.min(depth, MAX_VISIBLE_COLLAPSED - 1)
			const newestIdx = isTop.value ? 0 : visibleItems.value.length - 1
			const newestHeight = getItemHeight(visibleItems.value[newestIdx]?.id ?? '')
			const peek = asymptote(effectiveDepth, COLLAPSED_Y_MAX)
			return direction * (newestHeight + peek - COLLAPSED_CLIP_HEIGHT_PX)
		}
		// Expanded: sum heights of all newer items (depth < mine) plus gaps.
		let offset = 0
		for (let d = 0; d < depth; d++) {
			const idx = isTop.value ? d : visibleItems.value.length - 1 - d
			const item = visibleItems.value[idx]
			if (item) offset += getItemHeight(item.id) + EXPANDED_GAP_PX
		}
		return direction * offset
	}

	const scaleFor = (i: number) => {
		const depth = depthOf(i)
		if (!shouldStack.value) return 1
		return 1 - asymptote(depth, COLLAPSED_SCALE_MAX)
	}

	const opacityFor = (i: number) => {
		const depth = depthOf(i)
		// In collapsed stack, hide items beyond the visual cap (they're represented by the "+N more" pill)
		if (shouldStack.value && depth >= MAX_VISIBLE_COLLAPSED) return 0
		return 1
	}

	// In collapsed stack, older toasts become empty peek cards (just bg + type color bar).
	// Their content is hidden so no text/icons bleed through the peek strip.
	const isPeekCard = (i: number) => shouldStack.value && depthOf(i) !== 0

	const toastInnerStyle = (i: number) => {
		if (!isPeekCard(i)) return {}
		return {
			minHeight: `${COLLAPSED_CLIP_HEIGHT_PX}px`,
			maxHeight: `${COLLAPSED_CLIP_HEIGHT_PX}px`,
			overflow: 'hidden',
		}
	}

	const zIndexFor = (i: number) => 1000 + (visibleItems.value.length - depthOf(i))

	const itemPositionStyle = computed(() => {
		const style: Record<string, string> = { position: 'absolute' }
		if (isTop.value) style.top = '0'
		else style.bottom = '0'
		if (isCenter.value) {
			style.left = '0'
			style.right = '0'
		} else if (isRight.value) {
			style.right = '0'
		} else {
			style.left = '0'
		}
		return style
	})

	const itemStyle = (i: number) => ({
		...itemPositionStyle.value,
		zIndex: zIndexFor(i),
		transformOrigin: transformOriginStyle.value,
	})

	// Reserve pointer hover area for the EXPANDED height so the stack stays expanded while hovering.
	const reservedHeightPx = computed(() => {
		const count = visibleItems.value.length
		if (count === 0) return 0
		let total = 0
		for (const item of visibleItems.value) total += getItemHeight(item.id)
		return total + Math.max(0, count - 1) * EXPANDED_GAP_PX
	})

	// Counter pill shown outside the stack when more toasts exist than are visible
	const hiddenCount = computed(() => Math.max(0, items.value.length - MAX_VISIBLE))

	// Pill position follows the stack's far end (away from anchor):
	// - Bottom: above the stack (moves UP as stack expands)
	// - Top:    below the stack (moves DOWN as stack expands)
	// Uses collapsed extent when stacked, full extent when expanded → motion animates smoothly.
	const pillY = computed(() => {
		const count = visibleItems.value.length
		if (count === 0) return 0
		const direction = isTop.value ? 1 : -1
		const newestIdx = isTop.value ? 0 : count - 1
		const newestHeight = getItemHeight(visibleItems.value[newestIdx]?.id ?? '')

		if (shouldStack.value) {
			const maxDepth = Math.min(count - 1, MAX_VISIBLE_COLLAPSED - 1)
			const peek = asymptote(maxDepth, COLLAPSED_Y_MAX)
			return direction * (newestHeight + peek + 16)
		}

		let total = 0
		for (const item of visibleItems.value) total += getItemHeight(item.id)
		total += Math.max(0, count - 1) * EXPANDED_GAP_PX
		return direction * (total + 16)
	})

	const pillAnchorStyle = computed(() => {
		const style: Record<string, string | number> = {
			position: 'absolute',
			zIndex: 998,
		}
		if (isTop.value) style.top = '0'
		else style.bottom = '0'
		if (isCenter.value) style.left = '50%'
		else if (isRight.value) style.right = '0'
		else style.left = '0'
		return style
	})

	const pillAnimate = computed(() => {
		const base: Record<string, number | string> = {
			opacity: expanded.value ? 1 : 0,
			y: pillY.value,
		}
		if (isCenter.value) base.x = '-50%'
		return base
	})

	const iconFor = (type: ToastType) => {
		if (type === 'success') return CircleCheck
		if (type === 'error') return CircleX
		if (type === 'warning') return CircleAlert
		return Info
	}

	const iconColorFor = (type: ToastType) => {
		if (type === 'success') return 'text-success'
		if (type === 'error') return 'text-error'
		if (type === 'warning') return 'text-warning'
		return 'text-info'
	}

	const alertClassFor = (type: ToastType) => {
		if (type === 'success') return 'alert-success'
		if (type === 'error') return 'alert-error'
		if (type === 'warning') return 'alert-warning'
		return 'alert-info'
	}

	const handleAction = (item: ToastItem) => {
		item.action?.onClick()
		removeToast(item.id)
	}

	// Only the NEWEST toast has its auto-close timer running. Older ones pause until
	// the newer one is dismissed (and they become the newest themselves).
	// Also pause ALL timers while the stack is hovered/expanded, sync relative-time clock.
	const latestToastId = computed(() => items.value[items.value.length - 1]?.id)

	const isLatestToast = (item: ToastItem) => item.id === latestToastId.value

	const applyTimerStates = () => {
		const latestId = latestToastId.value
		const pauseAll = expanded.value || !windowFocused.value
		items.value.forEach((item) => {
			if (pauseAll || item.id !== latestId) pauseToast(item.id)
			else resumeToast(item.id)
		})
	}

	watch(expanded, (isExpanded) => {
		if (isExpanded) now.value = Date.now()
		applyTimerStates()
	})

	watch(latestToastId, () => {
		applyTimerStates()
	})

	watch(windowFocused, () => {
		applyTimerStates()
	})

	const COLLAPSE_DELAY_MS = 500
	let collapseTimer: ReturnType<typeof setTimeout> | null = null

	const handleContainerEnter = () => {
		if (collapseTimer) {
			clearTimeout(collapseTimer)
			collapseTimer = null
		}
		expanded.value = true
	}

	const handleContainerLeave = () => {
		if (collapseTimer) clearTimeout(collapseTimer)
		collapseTimer = setTimeout(() => {
			expanded.value = false
			collapseTimer = null
		}, COLLAPSE_DELAY_MS)
	}
</script>

<template>
	<Teleport to="body">
		<div
			class="pointer-events-none fixed z-[2000] w-[min(92vw,380px)]"
			:class="containerPositionClass"
			@pointerenter="handleContainerEnter"
			@pointerleave="handleContainerLeave">
			<div class="relative" :style="{ height: `${reservedHeightPx}px` }">
				<!-- Controls group (hidden count + clear all) — follows the stack's top edge -->
				<Motion
					v-if="items.length > 1"
					as="div"
					:initial="false"
					:animate="pillAnimate"
					:transition="{ type: 'spring', stiffness: 300, damping: 30, mass: 0.9 }"
					:style="pillAnchorStyle"
					class="bg-neutral text-neutral-content border-base-content/10 flex items-center gap-2.5 rounded-full border px-3 py-1.5 shadow-lg backdrop-blur-sm"
					:class="expanded ? 'pointer-events-auto' : 'pointer-events-none'">
					<span v-if="hiddenCount > 0" class="text-[0.65rem] font-semibold uppercase opacity-70">
						{{ t('toaster.more', { n: hiddenCount }) }}
					</span>
					<span v-if="hiddenCount > 0" class="h-3 w-px bg-current/25" />
					<button type="button" class="btn btn-attention btn-xs gap-1 rounded-full" @click="clearAllToasts">
						<Trash2 :size="11" />
						{{ t('toaster.clearAll') }}
					</button>
				</Motion>

				<AnimatePresence>
					<Motion
						v-for="(item, i) in visibleItems"
						:key="item.id"
						as="div"
						:initial="{ opacity: 1, scale: 0.9, y: yForItem(i), ...enterOffset }"
						:animate="{
							opacity: opacityFor(i),
							scale: scaleFor(i),
							y: yForItem(i),
							x: 0,
						}"
						:exit="{ opacity: 0, scale: 0.95, y: yForItem(i) }"
						:transition="{ type: 'spring', stiffness: 500, damping: 30, mass: 0.75 }"
						:style="itemStyle(i)"
						class="pointer-events-auto w-full">
						<div
							:ref="(el) => setItemRef(el as Element | null, item.id)"
							class="ring-base-content/10 alert relative items-start overflow-hidden py-3 pr-2 pl-4 shadow-xl ring transition-[max-height] duration-300 ease-out"
							:class="[alertClassFor(item.type), item.hardColor ? '' : 'alert-soft']"
							:style="toastInnerStyle(i)"
							role="alert">
							<template v-if="!isPeekCard(i)">
								<component :is="iconFor(item.type)" :size="18" class="shrink-0" />
								<div class="min-w-0 flex-1 space-y-1">
									<p class="text-sm leading-snug font-medium">{{ item.message }}</p>
									<p
										v-if="item.description"
										class="line-clamp-4 text-xs leading-snug text-current/70">
										{{ item.description }}
									</p>
									<div v-if="item.action || item.link" class="flex flex-wrap items-center gap-2 pt-1">
										<button
											v-if="item.action"
											type="button"
											class="btn btn-xs btn-neutral"
											@click="handleAction(item)">
											{{ item.action.label }}
										</button>
										<a
											v-if="item.link"
											:href="item.link.href"
											:target="item.link.target ?? '_blank'"
											rel="noopener noreferrer"
											class="link link-hover text-xs font-semibold"
											@click="removeToast(item.id)">
											{{ item.link.label }}
										</a>
									</div>
								</div>
								<button
									type="button"
									class="btn btn-ghost btn-xs btn-square shrink-0"
									aria-label="Close"
									@click="removeToast(item.id)">
									<X :size="14" />
								</button>
								<span
									v-if="relativeTime(item.createdAt)"
									class="pointer-events-none absolute right-2.5 bottom-1.5 font-mono text-[0.65rem] leading-none text-current/50">
									{{ relativeTime(item.createdAt) }}
								</span>
							</template>
							<div
								v-if="item.autoClose !== false"
								class="pointer-events-none absolute right-0 bottom-0 left-0 h-0.5 bg-current/10">
								<div
									class="toast-progress h-full origin-left bg-current/50"
									:style="{
										animationDuration: `${item.autoClose}ms`,
										animationPlayState:
											expanded || !windowFocused || !isLatestToast(item) ? 'paused' : 'running',
									}"
									@animationend="removeToast(item.id)" />
							</div>
						</div>
					</Motion>
				</AnimatePresence>
			</div>
		</div>
	</Teleport>
</template>

<style scoped>
	@keyframes toast-progress-keyframes {
		from {
			transform: scaleX(1);
		}
		to {
			transform: scaleX(0);
		}
	}

	.toast-progress {
		animation-name: toast-progress-keyframes;
		animation-timing-function: linear;
		animation-fill-mode: forwards;
	}
</style>
